/**
 * The three meanings `visibility === 'private'` carries (#158/#137).
 *
 * The data has always distinguished them via `review_state`; what was missing was
 * consistent presentation. Before this, the same three-way conditional was
 * copy-pasted into RestaurantCard, RestaurantDetail and UserProfile — and the
 * three copies had drifted: two distinguished "an admin unpublished this" from
 * "I marked this private myself" in their sub-text but badged both as plain
 * "Private", and the third didn't distinguish them at all.
 *
 * Returns null for a public restaurant (no badge anywhere), so callers can render
 * unconditionally on a truthy result.
 */

/** Detect the state, including the pre-#137 fallback where review_state is absent. */
export function ownershipStatus(restaurant) {
  if (!restaurant) return null;

  const reviewState = restaurant.review_state;
  const isPendingLegacy = reviewState === undefined && restaurant.is_approved === 0;

  if (reviewState === 'pending' || isPendingLegacy) {
    return {
      key: 'awaitingApproval',
      icon: '⏳',
      label: 'Awaiting approval',
      // Only state where something is still in flight — the one worth a colour
      // that draws the eye.
      variant: 'warning',
      inFlight: true,
      // Phrased as what happens NEXT: "is anyone waiting on me?" is the real
      // question behind all three states.
      hint: 'You submitted this for the public directory. An admin is reviewing it — nothing more is needed from you.',
    };
  }

  if (restaurant.visibility !== 'private') return null;

  if (reviewState === 'rejected') {
    return {
      key: 'madePrivate',
      icon: '🔒',
      label: 'Private (not published)',
      variant: 'secondary',
      inFlight: false,
      hint: "An admin didn't publish this to the public directory. It's still yours and still works for your own visits.",
    };
  }

  return {
    key: 'justForMe',
    icon: '🔒',
    label: 'Private',
    variant: 'secondary',
    inFlight: false,
    hint: 'You added this just for yourself. Only you can see it, and visits here stay private.',
  };
}

export default ownershipStatus;
